import React, { useState, useEffect } from 'react';
import { NeonButton } from './NeonButton';
import {
  LocalStorageService,
  LocalPlayerStats,
  LocalGameRecord,
  PlayerPreferences
} from '../services/localStorageService';

interface LocalStatsProps {
  playerName: string;
  onClose: () => void;
}

export const LocalStats: React.FC<LocalStatsProps> = ({ playerName, onClose }) => {
  const [stats, setStats] = useState<LocalPlayerStats | null>(null);
  const [history, setHistory] = useState<LocalGameRecord[]>([]);
  const [prefs, setPrefs] = useState<PlayerPreferences | null>(null);
  const [activeTab, setActiveTab] = useState<'stats' | 'history' | 'prefs'>('stats');

  useEffect(() => {
    setStats(LocalStorageService.getPlayerStats());
    setHistory(LocalStorageService.getPlayerHistory());
    setPrefs(LocalStorageService.getPlayerPreferences());
  }, []);

  const handleExport = () => {
    const data = LocalStorageService.exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gato-retro-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = LocalStorageService.importData(event.target?.result as string);
      if (result) {
        setStats(LocalStorageService.getPlayerStats());
        setHistory(LocalStorageService.getPlayerHistory());
        setPrefs(LocalStorageService.getPlayerPreferences());
        alert('Datos importados correctamente');
      } else {
        alert('Error al importar datos');
      }
    };
    reader.readAsText(file);
  };

  const handleClearData = () => {
    if (window.confirm('¿Estás seguro de que quieres borrar todos los datos locales?')) {
      LocalStorageService.clearAllData();
      setStats(LocalStorageService.getPlayerStats());
      setHistory(LocalStorageService.getPlayerHistory());
      setPrefs(LocalStorageService.getPlayerPreferences());
    }
  };

  const updatePref = (key: keyof PlayerPreferences, value: boolean) => {
    const newPrefs = LocalStorageService.savePlayerPreferences({ [key]: value });
    setPrefs(newPrefs);
  };

  const getResultEmoji = (result: string) => {
    switch (result) {
      case 'win': return '🏆';
      case 'loss': return '💀';
      case 'tie': return '🤝';
      default: return '❓';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="bg-retro-dark border-2 border-neon-cyan rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-neon-cyan/30">
          <h2 className="font-retro text-neon-yellow text-xl">
            📊 ESTADÍSTICAS LOCALES
          </h2>
          <button
            onClick={onClose}
            className="ui-button text-neon-pink hover:text-neon-pink/70 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="flex border-b border-neon-cyan/30">
          {(['stats', 'history', 'prefs'] as const).map((tab) => (
             <button
               key={tab}
               onClick={() => setActiveTab(tab)}
               className={`flex-1 py-3 font-retro text-sm transition-colors ${
                 activeTab === tab
                   ? 'bg-neon-cyan/20 text-neon-cyan border-b-2 border-neon-cyan'
                   : 'text-gray-400 hover:text-neon-cyan/70'
               }`}
             >
              {tab === 'stats' ? '📈 Stats' : tab === 'history' ? '📜 Historial' : '⚙️ Preferencias'}
            </button>
          ))}
        </div>

        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {activeTab === 'stats' && stats && (
            <div>
              <div className="mb-6">
                <p className="font-retro text-neon-cyan text-center text-lg mb-2">
                  {playerName}
                </p>
                <div className="text-center">
                  <span className="font-retro text-4xl text-neon-yellow">
                    {stats.winRate}%
                  </span>
                  <p className="font-retro text-neon-pink text-sm">TASA DE VICTORIA</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-neon-green/10 border border-neon-green rounded p-3 text-center">
                  <p className="font-retro text-3xl text-neon-green">{stats.wins}</p>
                  <p className="font-retro text-neon-green text-xs">VICTORIAS</p>
                </div>
                <div className="bg-neon-pink/10 border border-neon-pink rounded p-3 text-center">
                  <p className="font-retro text-3xl text-neon-pink">{stats.losses}</p>
                  <p className="font-retro text-neon-pink text-xs">DERROTAS</p>
                </div>
                <div className="bg-neon-yellow/10 border border-neon-yellow rounded p-3 text-center">
                  <p className="font-retro text-3xl text-neon-yellow">{stats.ties}</p>
                  <p className="font-retro text-neon-yellow text-xs">EMPATES</p>
                </div>
                <div className="bg-neon-cyan/10 border border-neon-cyan rounded p-3 text-center">
                  <p className="font-retro text-3xl text-neon-cyan">{stats.totalGames}</p>
                  <p className="font-retro text-neon-cyan text-xs">PARTIDAS</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-purple-500/10 border border-purple-500 rounded p-3 text-center">
                  <p className="font-retro text-2xl text-purple-400">{stats.currentStreak}</p>
                  <p className="font-retro text-purple-400 text-xs">RACHA ACTUAL</p>
                </div>
                <div className="bg-orange-500/10 border border-orange-500 rounded p-3 text-center">
                  <p className="font-retro text-2xl text-orange-400">{stats.bestStreak}</p>
                  <p className="font-retro text-orange-400 text-xs">MEJOR RACHA</p>
                </div>
              </div>

              {stats.lastPlayed && (
                <p className="font-retro text-gray-400 text-xs text-center">
                  Última partida: {formatDate(stats.lastPlayed)}
                </p>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              {history.length === 0 ? (
                <p className="font-retro text-gray-400 text-center py-8">
                  Sin partidas registradas aún
                </p>
              ) : (
                <div className="space-y-2">
                  {history.map((record) => (
                    <div
                      key={record.id}
                      className="flex items-center justify-between bg-retro-black/50 border border-neon-cyan/20 rounded p-3"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getResultEmoji(record.result)}</span>
                        <div>
                          <p className="font-retro text-neon-cyan text-sm">
                            vs {record.opponentName}
                          </p>
                          <p className="font-retro text-gray-500 text-xs">
                            {record.playerSymbol} • {formatDuration(record.duration)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-retro text-neon-yellow">
                          {record.playerScore} - {record.opponentScore}
                        </p>
                        <p className="font-retro text-gray-500 text-xs">
                          {formatDate(record.date)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'prefs' && prefs && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-retro text-neon-cyan">🔊 Sonidos</span>
                <button
                  onClick={() => updatePref('soundEnabled', !prefs.soundEnabled)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    prefs.soundEnabled ? 'bg-neon-green' : 'bg-gray-600'
                  }`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                    prefs.soundEnabled ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className="font-retro text-neon-cyan">✨ Animaciones</span>
                <button
                  onClick={() => updatePref('animationsEnabled', !prefs.animationsEnabled)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    prefs.animationsEnabled ? 'bg-neon-green' : 'bg-gray-600'
                  }`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                    prefs.animationsEnabled ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className="font-retro text-neon-cyan">🔄 Auto-conectar</span>
                <button
                  onClick={() => updatePref('autoConnect', !prefs.autoConnect)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    prefs.autoConnect ? 'bg-neon-green' : 'bg-gray-600'
                  }`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                    prefs.autoConnect ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className="font-retro text-neon-cyan">💾 Recordar nombre</span>
                <button
                  onClick={() => updatePref('rememberName', !prefs.rememberName)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    prefs.rememberName ? 'bg-neon-green' : 'bg-gray-600'
                  }`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                    prefs.rememberName ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div className="pt-4 border-t border-neon-cyan/20">
                <p className="font-retro text-neon-yellow text-sm mb-3">💾 Gestión de datos</p>
                <div className="flex gap-2 flex-wrap">
                  <NeonButton onClick={handleExport} color="cyan" className="text-sm">
                    Exportar
                  </NeonButton>
                  <label className="ui-button bg-neon-yellow/20 border-2 border-neon-yellow text-neon-yellow cursor-pointer hover:bg-neon-yellow/30">
                    Importar
                    <input type="file" accept=".json" onChange={handleImport} className="hidden" />
                  </label>
                  <NeonButton onClick={handleClearData} color="pink" className="text-sm">
                    Borrar todo
                  </NeonButton>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocalStats;
